/*******************************************************************************
* Licensed Materials - Property of HCL
* (c) Copyright HCL Technologies Ltd. 2020, 2023. All Rights Reserved.

* Note to U.S. Government Users Restricted Rights:
* Use, duplication or disclosure restricted by GSA ADP Schedule
**************************************************************/

/**
 * Component that creates a KPI Settings widget
 * required params:
 *  - setKeyboard
 *  - savedSettings
 *  - onCloseSettings
 *  - label
 *
 */

import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";
import { Button, Switch, Radio, Input, Text } from "vr-react-components";
import { handleSetKeyboard } from "../../handlers/PrivateHandlers";

function KPIWidgetSettings({
    width,
    height,
    savedSettings,
    onClose,
    label,
    fontColor = "#f2f2f2",
    ...otherAttributes
}) {
    const canModify = useSelector((state) => state.private.device.canModify);

    const [isInputFocused, setIsInputFocused] = useState(false);
    const [currentSettings, setCurrentSettings] = useState(
        savedSettings ? { ...savedSettings }
            : { radio: "Above", threshold: "", actionEnabled: false, },
    );

    useEffect(() => {
        return () => {
            handleSetKeyboard("us", false, null);
        }
    }, []);

    const storable = savedSettings &&
        currentSettings.threshold === savedSettings.threshold &&
        currentSettings.actionEnabled === savedSettings.actionEnabled &&
        currentSettings.radio === savedSettings.radio

    const handleSwitch = () => {
        setCurrentSettings((old) => {
            return { ...old, actionEnabled: !old.actionEnabled };
        });
    }

    const handleSetRadio = (option) => {
        setCurrentSettings((old) => {
            return { ...old, radio: option };
        });
    }

    const handleInput = () => {
        if (!currentSettings.actionEnabled) return;
        setIsInputFocused(true);

        handleSetKeyboard("num", true, (callback) => {
            setCurrentSettings((old) => {
                return { ...old, threshold: callback(old.threshold) };
            })
        })
    }

    const resetKeyboard = () => {
        handleSetKeyboard("num", false, null)
    }

    const handleClose = () => {
        resetKeyboard();
        onClose();
    }

    const handleSave = () => {
        resetKeyboard();
        onClose(currentSettings);
    }

    return (
        <a-entity {...otherAttributes}>
            <a-entity
                position={`${-width / 2 + width * 0.05} ${height / 2 - height * 0.05
                    } 0.001`}
            >
                <Text
                    text={label + " settings"}
                    width={width * 0.9}
                    align="left"
                    sizeDecimal={25 / width}
                    color={fontColor}
                    baseline="top"
                />
                <Text
                    text="Threshold notifications"
                    position={`0 ${(-height / 10) * 2} 0`}
                    width="3"
                    align="left"
                    sizeDecimal={30 / width}
                    color={fontColor}
                    baseline="center"
                />
                <Switch
                    onClick={handleSwitch}
                    status={currentSettings.actionEnabled}
                    aframeClasses={canModify ? "collidable" : ""}
                    position={`${width / 20} ${(-height / 10) * 3.5} 0`}
                    textWidth={width / 5}
                    textSizeDecimal={35 / width}
                    fontColor={fontColor}
                    width={width / 10}
                    height={height / 30}
                    radius={width / 30}
                />
                <Text
                    text="Trigger"
                    position={`0 ${(-height / 10) * 4.5} 0`}
                    width={width / 2}
                    align="left"
                    sizeDecimal={30 / width}
                    color={fontColor}
                    baseline="center"
                    opacity={currentSettings.actionEnabled ? "1" : "0.5"}
                />
                <Radio
                    setRadio={handleSetRadio}
                    position={`${width / 20} ${(-height / 10) * 5.5} 0`}
                    aframeClasses={canModify ? "collidable" : ""}
                    labels={["Above", "Below"]}
                    current={currentSettings.radio}
                    disabled={!currentSettings.actionEnabled}
                    textWidth={width / 2}
                    textSizeDecimal={35 / width}
                    radius={width / 30}
                    fontColor={fontColor}
                />
                <Text
                    text="Threshold"
                    position={`${((width * 0.9) / 4) * 3} ${(-height / 10) * 4.5} 0`}
                    width={width / 2}
                    align="center"
                    sizeDecimal={30 / width}
                    color={fontColor}
                    baseline="center"
                    opacity={currentSettings.actionEnabled ? "1" : "0.5"}
                />
                <Input
                    onClickHandler={handleInput}
                    focused={isInputFocused}
                    aframeClasses={canModify ? "collidable" : ""}
                    position={`${((width * 0.9) / 4) * 3} ${(-height / 10) * 6} 0`}
                    width={(width * 0.9) / 3}
                    height={height / 10}
                    text={currentSettings.threshold}
                    textSizeDecimal={30 / width}
                    disabled={!currentSettings.actionEnabled}
                />
                <Button
                    text="Close"
                    textSizeDecimal={30 / width}
                    aframeClasses={canModify ? "collidable" : ""}
                    position={`${(width * 0.9) / 2} ${(-height / 10) * 8} 0`}
                    width={width / 4}
                    height={height / 10}
                    onClickHandler={handleClose}
                />
                <Button
                    text="Save"
                    textSizeDecimal={30 / width}
                    aframeClasses={canModify ? "collidable" : ""}
                    position={`${((width * 0.9) / 4) * 3.5} ${(-height / 10) * 8} 0`}
                    width={width / 4}
                    height={height / 10}
                    onClickHandler={handleSave}
                    disabled={storable}
                    type={Button.constants.primary}
                />
            </a-entity>
        </a-entity>
    );
}

// requiring data
KPIWidgetSettings.propTypes = {
    onClose: PropTypes.func.isRequired,
    label: PropTypes.string.isRequired,
};

export default KPIWidgetSettings;
